import { ref } from "vue";
import { getAuthHeader } from "@/shared/authentication/authentication.ts";
import { UploadBlobResult } from "@/shared/environment/blob/blob.types.ts";

export interface UploadOptions {
    url: string;
    formData: FormData;
    onProgress?: (loaded: number, total: number, percent: number) => void;
}

export function useUploader() {
    const isUploading = ref(false);
    let currentXHR: XMLHttpRequest | null = null;

    const uploadBlob = async ({ url, formData, onProgress }: UploadOptions): Promise<UploadBlobResult> => {
        return new Promise<UploadBlobResult>((resolve, reject) => {
            if (!url) return reject("No upload URL provided");

            if (!formData) return reject("No form data");

            currentXHR = new XMLHttpRequest();

            currentXHR.upload.onprogress = (event) => {
                if (!event.lengthComputable) return;

                const percent = Math.round((event.loaded / event.total) * 100);
                if (onProgress) onProgress(event.loaded, event.total, percent);
            };

            currentXHR.onload = () => {
                isUploading.value = false;
                if (currentXHR!.status !== 201) {
                    reject(currentXHR!.statusText);
                    return;
                }

                resolve(JSON.parse(currentXHR!.responseText));
            };

            currentXHR.onerror = () => {
                isUploading.value = false;
                reject("Upload failed");
            };

            currentXHR.open("POST", url);
            currentXHR.setRequestHeader("Authorization", getAuthHeader());
            currentXHR.send(formData);

            isUploading.value = true;
        });
    };

    const abort = () => {
        if (!currentXHR) return;

        currentXHR.abort();
        isUploading.value = false;
    };

    return {
        uploadBlob,
        abort,
        isUploading,
    };
}
